/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Builds the pre-launch (early access) mobile navigation item list, inserting the
 * access item either appended (when early access completed) or in the middle.
 */

import { HomeIcon, LayersIcon, PersonIcon, ReaderIcon, QuestionMarkCircledIcon, ChatBubbleIcon } from '@/shared/ui';
import { FF_BLOG } from '@/shared/utils/feature-flag';
import type { TFunction } from 'i18next';
import type { NavItem } from './MobileNav.types';

interface BuildNavItemsArgs {
  t: TFunction;
  navigate: (path: string) => void;
  onScrollTo: (id: string) => void;
  earlyAccessCompleted: boolean;
}

export function buildPreLaunchNavItems({ t, navigate, onScrollTo, earlyAccessCompleted }: BuildNavItemsArgs): NavItem[] {
  const accessItem: NavItem = {
    id: 'access',
    label: t('navigation.access'),
    icon: PersonIcon,
    action: () => navigate('/early-access'),
  };

  const baseItems: NavItem[] = [
    { id: 'begin', label: t('navigation.home'), icon: HomeIcon, action: () => onScrollTo('begin') },
    { id: 'levels', label: t('navigation.plans'), icon: LayersIcon, action: () => onScrollTo('levels') },
    {
      id: 'recursos',
      label: t('navigation.resources'),
      icon: ReaderIcon,
      action: () => onScrollTo('recursos'),
      description: t('navigation.menuDescriptions.resources'),
    },
    {
      id: 'faq',
      label: t('navigation.faq'),
      icon: QuestionMarkCircledIcon,
      action: () => onScrollTo('faq'),
      description: t('navigation.menuDescriptions.faq'),
    },
    ...(FF_BLOG
      ? [
          {
            id: 'blog',
            label: t('navigation.blog'),
            icon: ChatBubbleIcon,
            action: () => navigate('/blog'),
            description: t('navigation.menuDescriptions.blog'),
          },
        ]
      : []),
  ];

  const mid = Math.floor(baseItems.length / 2);
  return earlyAccessCompleted
    ? [...baseItems, accessItem]
    : [...baseItems.slice(0, mid), accessItem, ...baseItems.slice(mid)];
}
